import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, HeartHandshake, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { PageHeader } from '../components/Shared';
import { usePitaLogo, PitaShareCardBlock } from '../components/PitaCTA';

export default function PitaSupporters() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const pitaLogo = usePitaLogo('support');

  useEffect(() => {
    api.get('/pita/supporters').then((r) => { setData(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  if (!loaded) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16" data-testid="pita-supporters-loading">
        <div className="h-8 w-64 bg-slate-100 rounded animate-pulse" />
        <div className="h-40 w-full bg-slate-100 rounded-xl animate-pulse mt-6" />
      </div>
    );
  }

  if (!data?.enabled) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="pita-supporters-unavailable">
        <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">This page isn't available yet</h1>
        <p className="text-sm text-slate-500 mt-2">Check back soon.</p>
        <Button asChild className="mt-5 h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="pita-supporters-home-btn">
          <Link to="/home">Back to Home</Link>
        </Button>
      </div>
    );
  }

  const groups = data.groups || [];

  return (
    <div data-testid="pita-supporters-page">
      <PageHeader
        kicker="PUBLIC ISSUE TRACKER APP (PITA)"
        title="Who Supports PITA"
        subtitle="Residents, community groups, and candidates across Kawartha Lakes who back better reporting, mapping, and follow-up tools. Grouped by ward."
        logo={pitaLogo?.url}
        logoAlt={pitaLogo?.alt}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 flex gap-3 mb-8" data-testid="pita-supporters-privacy-note">
          <ShieldCheck className="h-5 w-5 text-[#1a2744] shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed">
            Every supporter listed here gave explicit consent and was approved before appearing. Names show exactly the
            way each supporter chose, including pseudonyms and anonymous entries. Emails, phone numbers, and addresses
            are never shown. {data.official_note}
          </p>
        </div>

        {groups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center" data-testid="pita-supporters-empty">
            <HeartHandshake className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-[#1a2744]">Approved supporters appear here, grouped by ward.</p>
            <p className="text-sm text-slate-500 mt-1">Be the first to add your support.</p>
            <Button asChild className="mt-5 h-11 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="pita-supporters-empty-support-btn">
              <Link to="/support-pita">Support PITA</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-8" data-testid="pita-supporters-groups">
            {groups.map((g) => (
              <section key={g.key} data-testid={`pita-supporters-group-${g.key}`}>
                <h2 className="font-serif font-black text-xl text-[#1a2744] border-b-2 border-[#C9A84C]/50 pb-2">{g.label}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                  {g.supporters.map((s) => (
                    <Card key={s.id} data-testid={`pita-supporter-${s.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 rounded-full bg-[#1a2744]/5 flex items-center justify-center text-[#1a2744] font-serif font-bold shrink-0">
                            {(s.display_name || 'S')[0]}
                          </div>
                          <div className="min-w-0">
                            <p className="font-serif font-bold text-[#1a2744] truncate">{s.display_name}</p>
                            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                              <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{s.role_label}</Badge>
                              {s.organization && <span className="text-xs text-slate-500 truncate">{s.organization}</span>}
                            </div>
                          </div>
                        </div>
                        {s.statement && <p className="text-sm text-slate-600 mt-3 italic">“{s.statement}”</p>}
                        {s.is_official && (
                          <p className="text-[11px] text-slate-400 mt-2">Personal support for the PITA tool. Not a City of Kawartha Lakes endorsement.</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <div className="mt-10 mb-2">
          <PitaShareCardBlock />
        </div>

        <div className="mt-10 rounded-2xl bg-[#1a2744] p-6 sm:p-8 text-center" data-testid="pita-supporters-cta">
          <h2 className="text-white font-serif font-black text-xl">Add your name</h2>
          <p className="text-slate-300 text-sm mt-2">Support PITA and help show that Kawartha Lakes wants practical accountability tools.</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="h-11 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press justify-center" data-testid="pita-supporters-support-btn">
              <Link to="/support-pita">Support PITA</Link>
            </Button>
            <Button asChild variant="outline" className="h-11 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press justify-center" data-testid="pita-supporters-request-btn">
              <Link to="/pita/request-access">Request PITA Embed or Member Access</Link>
            </Button>
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-6 text-center" data-testid="pita-supporters-disclaimer">{data.disclaimer}</p>
      </div>
    </div>
  );
}
